/**
 * Development Logger
 *
 * Provides rich console logging for development environment
 * with detailed error information, stack traces, and context
 */

import {
  IErrorLogger,
  ErrorContext,
  ApiErrorContext,
  ReactErrorContext,
  ErrorBreadcrumb,
  ContextualError,
} from '@shared/types/ErrorLogging';

export class DevelopmentLogger implements IErrorLogger {
  private breadcrumbs: ErrorBreadcrumb[] = [];
  private userContext: { userId?: string; userEmail?: string } = {};

  /**
   * Logs an error with full development details
   */
  logError(
    error: Error | ContextualError,
    context?: Partial<ErrorContext>
  ): void {
    console.group('🚨 Error Logged');

    console.error('Error:', error);
    console.error('Message:', error.message);
    console.error('Stack:', error.stack);

    if (error instanceof ContextualError) {
      console.log('Error Context:', error.context);
      if (error.originalError) {
        console.error('Original Error:', error.originalError);
      }
    }

    if (context) {
      console.log('Additional Context:', context);
    }

    if (this.userContext.userId) {
      console.info('User Context:', this.userContext);
    }

    if (this.breadcrumbs.length > 0) {
      console.group('Breadcrumbs (Last 10):');
      this.breadcrumbs.slice(-10).forEach(breadcrumb => {
        const icon = this.getBreadcrumbIcon(breadcrumb.category);
        console.log(
          `${icon} [${new Date(breadcrumb.timestamp).toLocaleTimeString()}] ${breadcrumb.message}`,
          breadcrumb.data
        );
      });
      console.groupEnd();
    }

    console.trace('Call Stack');
    console.groupEnd();

    // Also log to browser's error reporting if available
    if (typeof reportError === 'function') {
      reportError(error);
    }
  }

  /**
   * Logs a warning with context
   */
  logWarning(message: string, context?: Partial<ErrorContext>): void {
    console.group('⚠️ Warning');
    console.warn(message);
    if (context) {
      console.log('Context:', context);
    }
    console.trace('Call Stack');
    console.groupEnd();
  }

  /**
   * Logs informational message
   */
  logInfo(message: string, context?: Partial<ErrorContext>): void {
    console.group('ℹ️ Info');
    console.info(message);
    if (context) {
      console.log('Context:', context);
    }
    console.groupEnd();
  }

  /**
   * Logs debug information
   */
  logDebug(message: string, context?: Partial<ErrorContext>): void {
    console.group('🐛 Debug');
    console.debug(message);
    if (context) {
      console.log('Context:', context);
    }
    console.trace('Call Stack');
    console.groupEnd();
  }

  /**
   * Logs API-specific errors with detailed request/response info
   */
  logApiError(error: Error, apiContext: ApiErrorContext): void {
    console.group('🌐 API Error');
    console.error('Error:', error);

    console.group('Request Details:');
    console.log('Method:', apiContext.method);
    console.log('Endpoint:', apiContext.endpoint);
    console.log('Headers:', apiContext.requestHeaders);
    if (apiContext.requestBody) {
      console.log('Body:', apiContext.requestBody);
    }
    console.groupEnd();

    console.group('Response Details:');
    console.log('Status:', apiContext.status, apiContext.statusText);
    console.log('Headers:', apiContext.responseHeaders);
    if (apiContext.responseBody) {
      console.log('Body:', apiContext.responseBody);
    }
    if (apiContext.duration) {
      console.log('Duration:', `${apiContext.duration}ms`);
    }
    console.groupEnd();

    console.trace('Call Stack');
    console.groupEnd();
  }

  /**
   * Logs React-specific errors with component details
   */
  logReactError(error: Error, reactContext: ReactErrorContext): void {
    console.group('⚛️ React Error');
    console.error('Error:', error);

    console.group('Component Details:');
    console.log('Component:', reactContext.componentName);
    console.log('Error Boundary:', reactContext.errorBoundary);
    if (reactContext.componentStack) {
      console.log('Component Stack:', reactContext.componentStack);
    }
    if (reactContext.props) {
      console.log('Props:', reactContext.props);
    }
    if (reactContext.state) {
      console.log('State:', reactContext.state);
    }
    if (reactContext.hooks) {
      console.log('Hooks:', reactContext.hooks);
    }
    console.groupEnd();

    console.trace('Call Stack');
    console.groupEnd();
  }

  /**
   * Adds a breadcrumb for tracking user actions
   */
  addBreadcrumb(breadcrumb: ErrorBreadcrumb): void {
    this.breadcrumbs.push(breadcrumb);

    // Keep only last 100 breadcrumbs
    if (this.breadcrumbs.length > 100) {
      this.breadcrumbs = this.breadcrumbs.slice(-100);
    }

    // Log breadcrumb in development for real-time tracking
    const icon = this.getBreadcrumbIcon(breadcrumb.category);
    console.log(
      `${icon} [${breadcrumb.level.toUpperCase()}] ${breadcrumb.message}`,
      breadcrumb.data
    );
  }

  /**
   * Sets user context for error tracking
   */
  setUserContext(userId: string, userEmail?: string): void {
    const newContext = { userId, userEmail };

    // Only update and log if the context has actually changed
    if (
      this.userContext.userId !== userId ||
      this.userContext.userEmail !== userEmail
    ) {
      this.userContext = newContext;
      console.info('🔑 User context set:', this.userContext);
    }
  }

  /**
   * Clears user context
   */
  clearUserContext(): void {
    this.userContext = {};
    console.info('🔑 User context cleared');
  }

  private getBreadcrumbIcon(category: ErrorBreadcrumb['category']): string {
    const icons = {
      navigation: '🧭',
      user_action: '👆',
      api_call: '🌐',
      error: '❌',
      info: 'ℹ️',
    };
    return icons[category] || '📝';
  }
}
